import { useState, useCallback, useMemo } from 'react';
import { ADMIN_ONBOARDING_STEPS, ADMIN_STEP_IDS } from '../types/adminOnboarding.types';

export const useAdminOnboardingSteps = () => {
  const [steps, setSteps] = useState(() => {
    const initialSteps = [...ADMIN_ONBOARDING_STEPS];
    // Set first step as active by default
    if (initialSteps.length > 0) {
      initialSteps[0].active = true;
    }
    return initialSteps;
  });

  const [activeStepId, setActiveStepId] = useState(ADMIN_STEP_IDS.WELCOME);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // Get current active step
  const activeStep = useMemo(() => {
    return steps.find(step => step.id === activeStepId);
  }, [steps, activeStepId]);

  // Check if all steps are completed
  const isAllComplete = useMemo(() => {
    return steps.every(step => completedSteps.has(step.id));
  }, [steps, completedSteps]);

  // Navigate to a specific step
  const navigateToStep = useCallback((stepId) => {
    setActiveStepId(stepId);
    setSteps(prevSteps => 
      prevSteps.map(step => ({
        ...step,
        active: step.id === stepId
      }))
    );
  }, []);

  // Mark a step as completed and navigate to next step
  const markStepComplete = useCallback((stepId) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));
    setSteps(prevSteps => 
      prevSteps.map(step => ({
        ...step,
        complete: step.id === stepId ? true : step.complete
      }))
    );
    
    // Auto-navigate to next step
    const currentIndex = steps.findIndex(step => step.id === stepId);
    if (currentIndex < steps.length - 1) {
      const nextStepId = steps[currentIndex + 1].id;
      setTimeout(() => {
        navigateToStep(nextStepId);
      }, 100); // Small delay to ensure state is updated
    }
  }, [steps, navigateToStep]);

  // Navigate to next step
  const goToNextStep = useCallback(() => {
    const currentIndex = steps.findIndex(step => step.id === activeStepId);
    if (currentIndex < steps.length - 1) {
      const nextStepId = steps[currentIndex + 1].id;
      navigateToStep(nextStepId);
    }
  }, [steps, activeStepId, navigateToStep]);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    const currentIndex = steps.findIndex(step => step.id === activeStepId);
    if (currentIndex > 0) {
      const previousStepId = steps[currentIndex - 1].id;
      navigateToStep(previousStepId);
    }
  }, [steps, activeStepId, navigateToStep]);

  // Check if navigation to a step is allowed
  const canNavigateToStep = useCallback((stepId) => {
    // Allow navigation to completed steps or current step
    const stepIndex = steps.findIndex(step => step.id === stepId);
    const currentIndex = steps.findIndex(step => step.id === activeStepId);
    
    // Can navigate to current step or any previous step
    // Can navigate to next step only if current step is completed
    if (stepIndex <= currentIndex) return true;
    if (stepIndex === currentIndex + 1 && completedSteps.has(activeStepId)) return true;
    
    return false;
  }, [steps, activeStepId, completedSteps]);

  // Reset all steps
  const resetSteps = useCallback(() => {
    setSteps([...ADMIN_ONBOARDING_STEPS]);
    setActiveStepId(ADMIN_STEP_IDS.WELCOME);
    setCompletedSteps(new Set());
  }, []);

  return {
    steps,
    setSteps,
    activeStepId,
    activeStep,
    completedSteps,
    isAllComplete,
    markStepComplete,
    navigateToStep,
    goToNextStep,
    goToPreviousStep,
    canNavigateToStep,
    resetSteps
  };
};